import React, { useEffect, useRef, useCallback, useMemo, useState, forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { OrderedSet, List, OrderedMap, Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import flow from 'lodash/fp/flow';
import { debounce } from 'throttle-debounce';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import { querySelector } from '@/domain/router';
import { saveBackSearchUrlAction } from '@/domain/env';
import { userIdSelector } from '@/domain/env/envSelector';
import {
  paymentsCountSelector,
  paymentsListListSelector,
  savedGridFiltersSelector,
  savedGridSortingSelector,
  gridCurrentColumnsStateSelector,
  gridColumnsScopeSelector,
  gridApplyTransactionSelector,
  getGridSearchAfterByPageIndexSelector,
} from '@/domain/payments/selectors';
import {
  paymentsGridApplyTransactionAction,
  paymentsGridColumnsAppliedAction,
  paymentsGridColumnsScopeChangedAction,
  paymentsGridDefaultColumnsAppliedAction,
  paymentsGridFilterAppliedAction,
  paymentsGridGetRowsAction,
  paymentsGridPaymentsByPageIndexAction,
  paymentsGridSearchAfterByPageIndexAction,
  paymentsGridSortingAppliedAction,
  setPaymentNavigationAction,
} from '@/domain/payments/actions';
import { agGridRefreshServerSideSelector } from '@/domain/agGrid/selectors';
import { agGridRefreshServerSideAction } from '@/domain/agGrid/actions';
import { GridApplyTransactionFactory } from '@/domain/documents/factories';
import {
  setExelModeFilter,
  addColDefaultParams,
  addHeaderTooltipPropsForColDefs,
  suppressSelectAll,
  isServiceColumn,
  GRID_FILTER_BUTTONS,
} from '@/components/AgGrid/helpers';
import {
  addSelectionMasterDetailToFirstColumn,
  addColumnsVisibilityColumnDef,
  addContextMenuToColumnsVisibilityColumnDef,
  addPaymentsPreviewColumnDef,
} from '@/components/AgGrid/Documents/serviceColumnDefs';
import * as ACL from '@/domain/restriction';
import { addFilters } from '@/components/AgGrid/helpers/filterHelpers';
import { getNestedCategory } from '@/domain/categories/helpers';
import { GRID_PAGE_SIZE } from '@/domain/documents/constants';
import { getPaymentUrl } from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/helpers';
import { getPaymentsGridPerPage, setPaymentsGridPerPage } from '@/domain/payments/helpers';
import {
  applyColumnLayout,
  getColumnLayoutScope,
  getColumnLayoutStorageKey,
  getColumnsSessionScope,
  getPaymentsGridColumnLayout,
  setPaymentsGridColumnLayout,
  toColumnLayout,
  toColumnOrderAndVisibility,
  toFitColumnLimits,
} from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/helpers/columnLayout';
import { PaymentsListAdapter } from '@/domain/payments/adapters';

// components
import { AgGridReact } from 'ag-grid-react';
import Box from '@mui/material/Box';
import ContextCellRenderer from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/components/ContextCellRenderer';
import AgGridCustomTooltip from '@/components/AgGrid/components/CustomTooltip';
import AgGridColumnHeader from '@/components/AgGrid/components/ColumnHeader';
import AmountSumStatusPanelRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/AmountSumStatusPanelRenderer';
import AmountCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/AmountCellRenderer';
import CustomSetColumnFilter from '@/components/AgGrid/components/CustomHeaderFilter/CustomSetColumnFilter/CustomSetColumnFilter';
import NoRowsOverlay from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/components/NoRowsOverlay';
import PreviewCellRenderer from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/components/PreviewCellRenderer';
import BulkActions from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/components/BulkActions';
import AgDateFilter from '@/components/AgGrid/components/AgDateFilter/AgDateFilterBE';
import DocumentContextMenu from '@/pages/company/WorkspaceRoute/components/DocumentContextMenu';
import LinksCellRenderer from '@/components/AgGrid/components/LinksCellRenderer';
import PaymentPreviewNavigation from '@/pages/dokkaPay/pages/company/components/PaymentPreviewNavigation';
import BulkActionPanelWrapper from '@/components/BulkActionPanelWrapper';
import AgGridGlobalStyles from '@/components/AgGrid/components/AgGridGlobalStyles';
import AgGridDocumentsStyles from '@/components/AgGrid/Documents/AgGridDocumentsStyles';
import ColorStringCellRenderer from '@/components/AgGrid/components/ColorStringCellRenderer';
import PayApprovalsCellRenderer from '@/components/AgGrid/components/ApprovalsCellRenderer/PayApprovalsCellRenderer';

import usePropagateGridApi from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/usePropagateGridApiRef';
import usePrevious from '@/hooks/usePrevious';
import useDidUpdateEffect from '@/hooks/useDidUpdateEffect';
import useScrollManager, { getWorkspaceScrollPosition } from '@/lib/scrollManager';
import useAgGridDocumentsProps from '@/components/AgGrid/Documents/useAgGridDocumentsProps';
import useColumnTypes from '@/components/AgGrid/Payments/useAgGridColumnTypes';
import useAgGridTranslations from '@/components/AgGrid/hooks/useAgGridTranslations';
import useAgGridRtlMethods from '@/components/AgGrid/hooks/useAgGridRtlMethods';
import useGridContext from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/useGridContext';
import useFetchGridColumnDefs from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/useFetchGridColumnDefs';
import useFetchTotalByCategory from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/useFetchTotalByCategory';
import useRangeSelectionChanged from '@/pages/company/WorkspaceRoute/components/Grid/useRangeSelectionChanged';
import useAgGridPaymentsPreviewNavigation from '@/components/AgGrid/Payments/useAgGridPaymentsPreviewNavigation';
import { useContextMenu } from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/hooks/useContextMenu';
import useContextMenuActions from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/useContextMenuActions';
import useAgGridTheme from '@/components/AgGrid/hooks/useAgGridTheme';
import usePaymentActionAbility from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/hooks/usePaymentActionAbility';

import Api from '@/domain/api';

import { filterMapping } from '@/components/AgGrid/Payments/filterHelpers';
import { filteredItem } from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/helpers/contextMenuHelper';

import {
  ModuleRegistry,
  GridStateModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ColumnApiModule,
  CellApiModule,
  ColumnAutoSizeModule,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  GroupFilterModule,
  MultiFilterModule,
  ServerSideRowModelModule,
  SetFilterModule,
  StatusBarModule,
  ClipboardModule,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  CellSelectionModule,
  StatusBarModule,
  ContextMenuModule,
  ColumnMenuModule,
  GridStateModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  CustomFilterModule,
  CellSelectionModule,
  ColumnApiModule,
  CellApiModule,
  ColumnAutoSizeModule,
  ClipboardModule,
]);

// colDef width set by addColDefaultParams
const DATA_COLUMN_DEFAULT_WIDTH = 200;
// keeps the header sort/filter/pin buttons and a few letters of the name visible
const DATA_COLUMN_MIN_WIDTH = 140;

const addDataColumnsMinWidth = (columns: any[]) =>
  columns.map((column) => (isServiceColumn(column.field) ? column : { ...column, minWidth: DATA_COLUMN_MIN_WIDTH }));

const Grid = forwardRef((_, ref) => {
  const dispatch = useDispatch();
  const routeParams = useParams();
  const { search } = useLocation();
  const agGridTheme = useAgGridTheme();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const DEFAULT_PER_PAGE = useMemo(() => getPaymentsGridPerPage(), []);

  const paymentsList = useSelector(paymentsListListSelector);
  const paymentsCount = useSelector(paymentsCountSelector);
  const refreshDocumentNodes = useSelector(gridApplyTransactionSelector);
  const refreshServerSide = useSelector(agGridRefreshServerSideSelector);
  const gridFilters = useSelector(savedGridFiltersSelector);
  const gridSorting = useSelector(savedGridSortingSelector);
  const gridColumnsState = useSelector(gridCurrentColumnsStateSelector);
  const gridSearchAfterByPageIndex = useSelector(getGridSearchAfterByPageIndexSelector);
  const restriction = useSelector(ACL.myRestriction);
  const query = useSelector(querySelector);
  const userId = useSelector(userIdSelector);
  const gridColumnsScope = useSelector(gridColumnsScopeSelector);
  const flowState = new URLSearchParams(search).get('flowState');
  const category = getNestedCategory(routeParams);
  const layoutStorageKey = getColumnLayoutStorageKey(userId, getColumnLayoutScope());
  // the layout is saved once per user, but the column state of the session belongs to one tab or category
  const sessionScope = getColumnsSessionScope(flowState, category);
  const sessionScopeRef = useRef(sessionScope);
  // columns the user resized since the last save: only they save a width
  const sizedColIdsRef = useRef<ReadonlySet<string>>(new Set());

  useEffect(() => {
    sessionScopeRef.current = sessionScope;
    sizedColIdsRef.current = new Set();
  }, [sessionScope]);

  // column changes kept in the store belong to one tab or category; another one starts from its saved layout
  useEffect(() => {
    if (gridColumnsScope !== sessionScope) {
      dispatch(paymentsGridColumnsScopeChangedAction(sessionScope));
    }
  }, [dispatch, gridColumnsScope, sessionScope]);

  const agGridDocumentsProps = useAgGridDocumentsProps();
  const { getLocaleText } = useAgGridTranslations();
  const { invertPinnedWhenRTL, enableRtl } = useAgGridRtlMethods();
  // Using queryRef to avoid stale closure of query in onPaymentOpen.
  // This keeps the value up-to-date without recreating the function.
  const queryRef = useRef(query);
  const columnTypes = useColumnTypes();
  const gridApiRef = useRef<any>(null);
  // !!! Ref.current used to avoid unnecessary prop changes and re-renders in ag-grid instead of getting this data from Redux after changes
  // !!! dispatched data to redux mostly needed for propagate(sharing) to another components
  const onFirstDataRenderedRef = useRef(false);
  const filterModelRef = useRef<any>(null); // workaround for not applied filter model in grid
  const previousFilterModelRef = useRef<any>({}); // used for detect if filter model has changes
  const previousSortModelRef = useRef<any[]>([]); // used for detect if sort model has changes
  const previousColumnModelRef = useRef<any[]>([]); // used for detect if column model has changes - [width, pinned, order, hide]
  const previousInitialColumnStateRef = useRef<any[]>([]); // used for detect if initial column state has changes
  const [selected, setSelected] = useState(OrderedSet());
  const previousSelectedSize = usePrevious(selected.size); // used for detect cmd/ctrl+A or escape
  const selectedIDsRef = useRef(selected);
  const searchAfterByPageIndexRef = useRef(Map()); // keys for navigation between pages, param used by b-end for pagination for huge list of documents(over 100k for example)
  // paymentsOrderedMapRef used for pagination, dispatch actual payments list to redux
  // actual payments for current page
  const paymentsOrderedMapRef = useRef(OrderedMap()); // OrderedMap with adapted payments by ID
  const [isGridInit, setGridInit] = useState(false);
  const [isFirstDataFetched, setIsFirstDataFetched] = useState(false);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [isDatasourceRequestInProgress, setDatasourceRequestInProgress] = useState(false);

  const onCellSelectionChanged = useRangeSelectionChanged(gridApiRef);

  const { fetchGridTotal, paymentsTotal, paymentsTotalIsBusy, paymentsTotalIsFetched } = useFetchTotalByCategory();
  const { rawColumnDefsIsBusy, rawColumnDefs } = useFetchGridColumnDefs(queryRef.current);

  const defaultColumnDefs = useMemo(() => {
    if (rawColumnDefsIsBusy) return [];

    const columns = flow([
      addPaymentsPreviewColumnDef,
      addSelectionMasterDetailToFirstColumn,
      addColumnsVisibilityColumnDef,
      addContextMenuToColumnsVisibilityColumnDef,
      addFilters(filterMapping),
      setExelModeFilter('windows', [GRID_FILTER_BUTTONS.APPLY, GRID_FILTER_BUTTONS.RESET]),
      addColDefaultParams,
      addDataColumnsMinWidth,
      addHeaderTooltipPropsForColDefs,
      // read once per column defs fetch (tab switch, reload) - saving a change must not rebuild the grid columns
      (cols: Array<{ field: string; hide?: boolean; pinned?: 'left' | 'right' | null; width?: number }>) =>
        applyColumnLayout(cols, getPaymentsGridColumnLayout(layoutStorageKey)),
    ])(rawColumnDefs);

    previousColumnModelRef.current = columns;

    return columns;
  }, [rawColumnDefsIsBusy, rawColumnDefs, layoutStorageKey]);

  const columnDefs = useMemo(
    () => flow(suppressSelectAll, invertPinnedWhenRTL)(defaultColumnDefs),
    [defaultColumnDefs, invertPinnedWhenRTL],
  );

  const initialColumnState = useMemo(
    () =>
      defaultColumnDefs.map(({ field, hide = false, pinned, width = 0, minWidth, maxWidth }: any) => {
        const currentWidth = Math.max(minWidth || 1, Math.min(maxWidth || 1000, width));

        return {
          aggFunc: null,
          colId: field,
          flex: null,
          hide,
          pinned: pinned || null,
          pivot: false,
          pivotIndex: null,
          rowGroup: false,
          rowGroupIndex: null,
          sort: null,
          sortIndex: null,
          width: currentWidth,
        };
      }),
    [defaultColumnDefs],
  );

  const initialState = useMemo(() => {
    const { pageIndex = 0 } = getWorkspaceScrollPosition();

    return {
      filter: {
        filterModel: gridFilters,
      },
      sort: {
        sortModel: gridSorting,
      },
      pagination: { page: pageIndex, pageSize: perPage },
      partialColumnState: true,
    };
  }, [gridSorting, gridFilters, perPage]);

  useEffect(() => {
    if (isFirstDataFetched && !paymentsTotalIsBusy && paymentsTotal === 0 && !isDatasourceRequestInProgress) {
      gridApiRef.current?.showNoRowsOverlay();
    }
  }, [paymentsTotal, isFirstDataFetched, isDatasourceRequestInProgress, paymentsTotalIsBusy]);

  useEffect(() => {
    if (
      rawColumnDefs.length > 0 &&
      initialColumnState.length > 0 &&
      !isEqual(previousInitialColumnStateRef.current, initialColumnState)
    ) {
      previousInitialColumnStateRef.current = initialColumnState;
      previousColumnModelRef.current = initialColumnState;
      dispatch(paymentsGridDefaultColumnsAppliedAction(initialColumnState));
    }
  }, [dispatch, initialColumnState, rawColumnDefs]);

  // !IMPORTANT - dont change order of useEffect below for applying grid state
  useEffect(() => {
    if (isGridInit) {
      if (!isEqual(previousFilterModelRef.current, gridFilters)) {
        gridApiRef.current?.setFilterModel(gridFilters);
        filterModelRef.current = gridFilters;
        previousFilterModelRef.current = gridFilters;
      }
    }
  }, [isGridInit, gridFilters]);

  // sizeColumnsToFit starts from the colDef width, so with the same defaultMinWidth it only fills free space
  // and a grid wider than the screen keeps its horizontal scroll; the larger of defaultMinWidth and the colDef
  // minWidth applies, so 140px limits manual resizing only; a width the user saved is that column's minimum
  const fitColumnsToGridWidth = useMemo(
    () =>
      debounce(100, false, () => {
        const api = gridApiRef.current;
        if (api && !api.isDestroyed()) {
          api.sizeColumnsToFit({
            defaultMinWidth: DATA_COLUMN_DEFAULT_WIDTH,
            columnLimits: toFitColumnLimits(getPaymentsGridColumnLayout(layoutStorageKey)).filter(({ key }) =>
              api.getColumn(key),
            ),
          });
        }
      }),
    [layoutStorageKey],
  );

  useEffect(() => {
    if (isGridInit) {
      if (!isEqual(previousColumnModelRef.current, gridColumnsState)) {
        // skip sort props - we apply sort via gridSorting state
        const state = invertPinnedWhenRTL(gridColumnsState).map(({ colId, pinned, width, hide, flex }: any) => ({
          colId,
          pinned,
          width,
          hide,
          flex,
        }));

        gridApiRef.current?.applyColumnState({
          state,
          applyOrder: true,
        });
        previousColumnModelRef.current = gridColumnsState;
        fitColumnsToGridWidth();
      }
    }
  }, [isGridInit, gridColumnsState, invertPinnedWhenRTL, fitColumnsToGridWidth]);

  useEffect(() => {
    if (isGridInit) {
      if (!isEqual(previousSortModelRef.current, gridSorting)) {
        const previousSort = previousSortModelRef.current[0];
        const currentSort = gridSorting[0];

        const state = [
          {
            sort: null,
            colId: previousSort?.colId,
          },
          {
            sort: currentSort?.sort || null,
            colId: currentSort?.colId,
          },
        ];

        gridApiRef.current?.applyColumnState({
          state,
        });

        previousSortModelRef.current = gridSorting;
      }
    }
  }, [isGridInit, gridSorting]);

  useEffect(() => {
    if (gridApiRef.current && paymentsOrderedMapRef.current.size > 0) {
      // this is for cmd/ctrl+A feature, listener is placed in BulkPanelComponent,
      // 'api' - send this source name for event, and skip handler for this
      // reset selected rows when we have empty array list
      if (selected.size === 0 && previousSelectedSize) {
        gridApiRef.current?.deselectAll('api');
        selectedIDsRef.current = OrderedSet();
      } else if (selected.size === paymentsOrderedMapRef.current.size && previousSelectedSize !== selected.size) {
        gridApiRef.current?.selectAll('api');
        selectedIDsRef.current = OrderedSet(selected);
      }
    }
  }, [selected, previousSelectedSize]);

  // used for refresh data(silent mode - no request sent to backend) of grid after some changes for example moving to another category etc
  // dispatched in saga mostly
  useEffect(() => {
    const { nodes, transactionType } = refreshDocumentNodes;
    if (transactionType && nodes.size > 0) {
      const data = refreshDocumentNodes.nodes;
      const transaction = {
        [refreshDocumentNodes.transactionType]: data,
      };

      const result = gridApiRef.current?.applyServerSideTransaction(transaction);
      const status = result?.status;

      if (transactionType === 'remove' && status === 'Applied') {
        paymentsOrderedMapRef.current = paymentsOrderedMapRef.current.filter((p: any) => !nodes.includes(p.id));
        fetchGridTotal().then();
      }
      dispatch(paymentsGridApplyTransactionAction(GridApplyTransactionFactory()));
    }
  }, [refreshDocumentNodes, dispatch, selected, fetchGridTotal]);

  // used for refresh data(hard mode - request sent to backend) of grid after some changes for example moving to another category etc
  useEffect(() => {
    if (typeof refreshServerSide === 'boolean') {
      gridApiRef.current?.deselectAll();
      gridApiRef.current?.refreshServerSide({ purge: refreshServerSide });
      dispatch(agGridRefreshServerSideAction(null));
      fetchGridTotal().then();
    }
  }, [refreshServerSide, dispatch, fetchGridTotal]);

  useEffect(() => {
    // when we come to document page from grid view and navigate between documents
    // we can return to the grid view, set Ref value from redux for navigate to correct page on grid
    if (!gridSearchAfterByPageIndex.equals(searchAfterByPageIndexRef.current)) {
      searchAfterByPageIndexRef.current = gridSearchAfterByPageIndex;
    }
  }, [gridSearchAfterByPageIndex]);

  const onClearSelected = useCallback(() => {
    setSelected(OrderedSet());
  }, [setSelected]);

  // refresh grid data when category changed and skip it on first render
  useDidUpdateEffect(() => {
    gridApiRef.current?.deselectAll();
    onClearSelected();
    gridApiRef.current?.refreshServerSide({ purge: true });
  }, [category]);

  // refresh grid data when [location search (URL query params)] changed and skip it on first render
  useDidUpdateEffect(() => {
    onClearSelected();
    gridApiRef.current?.refreshServerSide({ purge: false });
  }, [search]);

  const getRowID = useCallback(({ data }: { data: any }) => data.id, []);

  const dispatchColumnState = useCallback(() => {
    // a save debounced on another tab or category must not land on the one shown now
    const isCurrentScope = sessionScopeRef.current === sessionScope;
    if (gridApiRef.current && !gridApiRef.current.isDestroyed() && isCurrentScope) {
      const columns = gridApiRef.current?.getColumnState();
      // we save to store always LTR version of grid state
      const stateLTR = invertPinnedWhenRTL(columns);
      // we compare with gridColumnState in ltr variant, so set to previousColumnModelRef.current ltr variant as well
      previousColumnModelRef.current = stateLTR;
      dispatch(paymentsGridColumnsAppliedAction(stateLTR));
      setPaymentsGridColumnLayout(layoutStorageKey, toColumnLayout(stateLTR), sizedColIdsRef.current);
      // only a save clears them: a stale save of another tab must not drop the resizes made on this one
      sizedColIdsRef.current = new Set();
    }
  }, [dispatch, invertPinnedWhenRTL, layoutStorageKey, sessionScope]);

  const onSelectionChanged = useCallback(({ api, source }: { api: any; source: any }) => {
    // event source 'api' is set in useEffect
    if (source !== 'api') {
      let selectedIDs;
      const { selectAll, toggledNodes } = api.getServerSideSelectionState();

      const paymentsIDs = paymentsOrderedMapRef.current.keySeq().toOrderedSet();

      if (selectAll) {
        if (toggledNodes.length > 0) {
          selectedIDs = OrderedSet(paymentsIDs).subtract(toggledNodes);
        } else {
          selectedIDs = OrderedSet(paymentsIDs);
        }
      } else {
        selectedIDs = OrderedSet(toggledNodes);
      }

      if (!selectedIDs.equals(selectedIDsRef.current)) {
        selectedIDsRef.current = selectedIDs;
        setSelected(selectedIDs);
      }
    }
  }, []);

  // filter before debouncing: the debounce keeps the last call, and sizeColumnsToFit resizes follow a drag
  const debouncedDispatchColumnState = useMemo(() => debounce(250, false, dispatchColumnState), [dispatchColumnState]);

  const handleChangeColumnState = useCallback(
    ({
      type,
      source,
      columns,
    }: {
      type: any;
      source: any;
      columns?: ReadonlyArray<{ getColId: () => string }> | null;
    }) => {
      if (type === 'columnResized' && (source === 'uiColumnResized' || source === 'autosizeColumns')) {
        sizedColIdsRef.current = new Set([...sizedColIdsRef.current, ...(columns ?? []).map((c) => c.getColId())]);
      }
      // dragStopped - column drag, resize column
      // autosizeColumns - via column menu (generalMenuTab)
      if (type === 'dragStopped' || source === 'autosizeColumns') {
        debouncedDispatchColumnState();
      }
    },
    [debouncedDispatchColumnState],
  );

  const onColumnPinned = useCallback(
    ({ source }: { source: any }) => {
      // dispatch column state only when done manually via menu
      // skip when we apply state via api
      if (source === 'columnMenu') {
        dispatchColumnState();
      }
    },
    [dispatchColumnState],
  );

  const findColumnToUnpin = useCallback(
    (columnState: any[], pinPosition: string | null) =>
      columnState.find((col: any) => !col.colId.startsWith('__SERVICE') && col.pinned === pinPosition),
    [],
  );

  const handleProcessUnpinnedColumns = useCallback(
    ({ api }: { api: any }) => {
      const columnState = api.getColumnState();

      let columnToUnpin = findColumnToUnpin(columnState, enableRtl ? 'left' : 'right');

      if (!columnToUnpin) {
        columnToUnpin = findColumnToUnpin(columnState, enableRtl ? 'right' : 'left');
      }

      if (columnToUnpin) {
        api.setColumnsPinned([columnToUnpin.colId], null);
        dispatchColumnState();
      }
    },
    [dispatchColumnState, enableRtl, findColumnToUnpin],
  );

  const onColumnMenuVisibleChanged = useCallback(
    ({ visible, key, api }: { visible: any; key: any; api: any }) => {
      // when click on column menu button for management columns -
      // event return sometimes key generalMenuTab instead of columnsMenuTab
      if (visible === false && ['columnsMenuTab', 'generalMenuTab'].includes(key)) {
        // the column menu changes visibility and order of columns
        const previousLayout = toColumnOrderAndVisibility(previousColumnModelRef.current);
        const currentLayout = toColumnOrderAndVisibility(api.getColumnState());

        if (!isEqual(previousLayout, currentLayout)) {
          dispatchColumnState();
        }
      }
    },
    [dispatchColumnState],
  );
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const onPaymentOpen = useCallback(
    (paymentId: string) => {
      dispatch(
        saveBackSearchUrlAction({
          query: queryRef.current,
          routeParams,
        }),
      );
      dispatch(
        setPaymentNavigationAction({
          search: { query: queryRef.current, params: routeParams },
          isSetRequest: true,
        }),
      );
      const paymentURL = getPaymentUrl(routeParams.companyId!, paymentId);
      navigate(paymentURL);
    },
    [dispatch, routeParams, navigate],
  );

  const frameworkComponents = useMemo(
    () => ({
      amountCellRenderer: AmountCellRenderer,
      amountSumStatusPanel: AmountSumStatusPanelRenderer,
      previewCellRenderer: PreviewCellRenderer,
      contextCellRenderer: ContextCellRenderer,
      customSetColumnFilter: CustomSetColumnFilter,
      agColumnHeader: AgGridColumnHeader,
      customTooltip: AgGridCustomTooltip,
      customDateFilter: AgDateFilter,
      linksCellRenderer: LinksCellRenderer,
      colorStringCellRenderer: ColorStringCellRenderer,
      approvalsCellRenderer: PayApprovalsCellRenderer,
    }),
    [],
  );

  const serverDatasource = useMemo(
    () => ({
      getRows: async (params: any) => {
        const {
          context: { fetchParams },
          success,
          fail,
          request,
          api,
        } = params;
        const currentPageIndex = api.paginationGetCurrentPage();
        const { filterModel: requestFilterModel, sortModel, startRow, endRow } = request;
        const requestPageIndex = startRow / (endRow - startRow);
        // sometimes we can caught case when filter model(width custom/async filters) not applied
        // we got empty object instead of object with applied filter model
        // for safety, check of the applied model has been added
        const isAppliedFilterModel = isEqual(filterModelRef.current, requestFilterModel);

        if (isAppliedFilterModel) {
          filterModelRef.current = null;
        }

        const filterModel =
          filterModelRef.current && !isAppliedFilterModel ? filterModelRef.current : requestFilterModel;

        const requestData = {
          filterModel,
          sortModel,
          paginationModel: {
            limit: endRow - startRow,
            offset: startRow,
          },
          // startRow,
          // endRow,
        };

        api.hideOverlay();
        api.clearCellSelection();

        const isSortEqual = isEqual(previousSortModelRef.current, sortModel);
        const isFilterEqual = isEqual(previousFilterModelRef.current, filterModel);

        if (!isSortEqual) {
          // order important, set to previous, after dispatch to redux
          previousSortModelRef.current = sortModel;
          dispatch(
            paymentsGridSortingAppliedAction({
              sorting: sortModel,
            }),
          );
        }

        if (!isFilterEqual) {
          previousFilterModelRef.current = filterModel;
          dispatch(
            paymentsGridFilterAppliedAction({
              filters: filterModel,
            }),
          );
        }

        setDatasourceRequestInProgress(true);

        try {
          const {
            // data: { items, count, timeStamp, searchAfter },
            data: { items, count },
          } = await Api.getPaymentsGridRows({
            data: {
              ...fetchParams,
              ...requestData,
              searchAfter:
                currentPageIndex > 0 ? searchAfterByPageIndexRef.current.get((currentPageIndex - 1).toString()) : null,
            },
          });

          const adaptedList = PaymentsListAdapter(items);
          const response = { rowData: items, rowCount: count };
          success(response);

          paymentsOrderedMapRef.current = adaptedList;
          // TODO need BE!!!!!!
          searchAfterByPageIndexRef.current = searchAfterByPageIndexRef.current.set(
            currentPageIndex.toString(),
            List([]),
            // List(searchAfter),
          );

          dispatch({
            type: paymentsGridGetRowsAction.success,
            payload: {
              list: adaptedList,
              count,
            },
          });

          dispatch(
            paymentsGridPaymentsByPageIndexAction({
              data: Map().set(requestPageIndex.toString(), adaptedList.keySeq().toList()),
              purge: true,
            }),
          );

          // TODO need BE!!!!!!!!
          dispatch(
            paymentsGridSearchAfterByPageIndexAction({
              purge: false,
              data: Map().set(currentPageIndex.toString(), List([])),
              // data: Map().set(currentPageIndex.toString(), List(searchAfter)),
            }),
          );

          setDatasourceRequestInProgress(false);
          setIsFirstDataFetched(true);
        } catch (error) {
          console.log('====== REQUEST FAILED', error);
          fail();
        }
      },
    }),
    [dispatch], // be careful when adding dependencies to hook - potentially invoked again every time values change
  );

  const onGridReady = useCallback(async (params: any) => {
    gridApiRef.current = params.api;
    setGridInit(true);
  }, []);

  const onFirstDataRendered = useCallback(() => {
    onFirstDataRenderedRef.current = true;
  }, []);

  const onPaginationChanged = useCallback(
    ({ newPage, newPageSize, api }: { newPage: any; newPageSize: any; api: any }) => {
      if (newPageSize) {
        const size = api.paginationGetPageSize();

        api.deselectAll();
        setPerPage(size);
        setPaymentsGridPerPage(size);
      }

      if (newPage) {
        api.deselectAll();
      }
    },
    [],
  );

  const onSortChanged = useCallback(
    ({ api, source }: { api: any; source: any }) => {
      const currentPageIndex = api.paginationGetCurrentPage();

      // for b-end pagination we must reset user to the first page when start manual sorting
      if (source === 'uiColumnSorted' && currentPageIndex > 0) {
        dispatch(
          paymentsGridSearchAfterByPageIndexAction({
            purge: true,
            data: Map(),
          }),
        );

        searchAfterByPageIndexRef.current = Map();

        api.paginationGoToFirstPage();
      }
    },
    [dispatch],
  );

  const onStateUpdated = useCallback(({ state, sources, api }: { state: any; sources: any; api: any }) => {
    // console.log('====== ON STATE UPDATED', state, sources);
  }, []);

  const { startIndex, handlePreviewNavigate, handleChangePreview, isPreviewOpen, previewState } =
    useAgGridPaymentsPreviewNavigation({
      gridApiRef,
      isGridReady: isGridInit,
      list: paymentsList,
    });

  const { handleContextMenu, menu, contextMenuHide } = useContextMenu();
  const {
    handleStartApprovalFlow,
    handleArchive,
    handleUnarchive,
    handleSaveForLater,
    handleStartPaymentProcess,
    handleApprove,
    handleResetApprovalFlow,
    handlePay,
    handleDelete,
  } = useContextMenuActions();

  const paymentData = useMemo(() => paymentsList.find((p: any) => p?.id === menu?.id), [paymentsList, menu?.id]);

  const singlePayment = useMemo(
    () => (paymentData ? Map({ [paymentData.id]: paymentData }) : Map()) as any,
    [paymentData],
  );

  const {
    canStartApprovalFlow,
    canApprove,
    canStartPaymentProcess,
    canSaveForLater,
    canArchive,
    canUnarchive,
    canPay,
    canResetApprovalFlow,
    canDelete,
    isVisibleApprovalFlowButtons,
    isVisibleSendToApprovalButton,
    isVisibleStartPaymentProcessButton,
    isVisibleResetApprovalFlowButton,
    isVisiblePayButton,
    isVisibleSaveForLaterButton,
    isVisibleArchiveButton,
    isVisibleUnarchiveButton,
    isVisibleDeleteButton,
  } = usePaymentActionAbility(singlePayment);

  const contextMenuItems = useMemo(() => {
    const menuId = menu?.id ?? '';
    const items = {
      open: {
        action: () => {
          onPaymentOpen(menuId);
        },
      },
      preview: {
        action: () => {
          handleChangePreview(menuId);
        },
        hidden: previewState.id,
      },
      startPaymentProcess: {
        action: () => {
          handleStartPaymentProcess(menuId);
        },
        hidden: !isVisibleStartPaymentProcessButton,
        disabled: !canStartPaymentProcess,
      },
      sendToApproval: {
        action: () => {
          handleStartApprovalFlow(menuId);
        },
        hidden: !isVisibleSendToApprovalButton,
        disabled: !canStartApprovalFlow,
      },
      approve: {
        action: () => {
          handleApprove(menuId);
        },
        hidden: !isVisibleApprovalFlowButtons,
        disabled: !canApprove,
      },
      pay: {
        action: () => {
          handlePay(menuId, paymentData?.totalAmount ?? '', paymentData?.currency);
        },
        hidden: !isVisiblePayButton,
        disabled: !canPay,
      },
      saveForLater: {
        action: () => {
          handleSaveForLater(menuId);
        },
        hidden: !isVisibleSaveForLaterButton,
        disabled: !canSaveForLater,
      },
      resetApprovalFlow: {
        action: () => {
          handleResetApprovalFlow(menuId);
        },
        hidden: !isVisibleResetApprovalFlowButton,
        disabled: !canResetApprovalFlow,
      },
      archive: {
        action: () => {
          handleArchive(menuId);
        },
        hidden: !isVisibleArchiveButton,
        disabled: !canArchive,
      },
      unarchive: {
        action: () => {
          handleUnarchive(menuId);
        },
        hidden: !isVisibleUnarchiveButton,
        disabled: !canUnarchive,
      },
      deletePayment: {
        action: () => {
          handleDelete(menuId);
        },
        hidden: !isVisibleDeleteButton,
        disabled: !canDelete,
      },
    };

    return filteredItem({ restriction }).map((o: any) => ({ ...o, ...items[o.id as keyof typeof items] }));
  }, [
    restriction,
    previewState.id,
    handleChangePreview,
    menu?.id,
    paymentData?.totalAmount,
    paymentData?.currency,
    onPaymentOpen,
    handleStartApprovalFlow,
    handleStartPaymentProcess,
    handleApprove,
    handlePay,
    handleSaveForLater,
    handleResetApprovalFlow,
    handleArchive,
    handleUnarchive,
    handleDelete,
    canStartApprovalFlow,
    canApprove,
    canStartPaymentProcess,
    canSaveForLater,
    canArchive,
    canUnarchive,
    canPay,
    canResetApprovalFlow,
    canDelete,
    isVisibleApprovalFlowButtons,
    isVisibleSendToApprovalButton,
    isVisibleStartPaymentProcessButton,
    isVisibleResetApprovalFlowButton,
    isVisiblePayButton,
    isVisibleSaveForLaterButton,
    isVisibleArchiveButton,
    isVisibleUnarchiveButton,
    isVisibleDeleteButton,
  ]);

  const handleSelectAll = useCallback(() => {
    const paymentsIDs = paymentsOrderedMapRef.current.keySeq().toOrderedSet();
    const selectedIDs = OrderedSet(paymentsIDs);
    if (!selectedIDs.equals(selectedIDsRef.current)) {
      selectedIDsRef.current = selectedIDs;
      setSelected(selectedIDs);
    }
  }, []);

  const componentsContext = useMemo(
    () => ({
      previewCellRenderer: {
        onClickPreview: handleChangePreview,
        onClickPayment: onPaymentOpen,
      },
      contextCellRenderer: {
        oncClickContextMenu: handleContextMenu,
      },
    }),
    [handleChangePreview, onPaymentOpen, handleContextMenu],
  );

  const gridContext = useGridContext({
    components: componentsContext,
    autocompleteApiInstance: Api.getPaymentsGridFilterListByField,
  });

  usePropagateGridApi({ ref, gridApiRef, gridContext, selectedIDs: selected });

  useScrollManager({
    view: 'grid',
    isTargetLoaded: isFirstDataFetched,
    gridApi: gridApiRef.current,
  });

  // !IMPORTANT - be careful when changing next condition, we must wait for this data for grid to work correctly
  if (rawColumnDefsIsBusy || !paymentsTotalIsFetched) {
    return null;
  }

  return (
    <>
      <AgGridGlobalStyles />
      <AgGridDocumentsStyles firstPinnedCellIndex={1} />
      <Box
        className="ag-theme-dokka"
        display="flex"
        flexDirection="column"
        height="100%"
        mt={2}
        sx={{
          // gap from sidebar only for grid body, paging panel stays full width
          // marginLeft is mirrored to the right side for RTL by stylis-plugin-rtl
          '& .ag-root-wrapper-body': { marginLeft: 3 },
          // .ag-header-cell outweighs the centered header label of the global grid styles
          '& .ag-header-cell .ag-header-cell-label': { justifyContent: 'flex-start' },
        }}
      >
        <AgGridReact
          {...({
            theme: agGridTheme,
            serverSideDatasource: serverDatasource,
            components: frameworkComponents,
            detailRowHeight: 400,
            onGridReady: onGridReady,
            onFirstDataRendered: onFirstDataRendered,
            onStateUpdated: onStateUpdated,
            onColumnPinned: onColumnPinned,
            onSelectionChanged: onSelectionChanged,
            onDragStopped: handleChangeColumnState,
            onCellSelectionChanged: onCellSelectionChanged,
            onColumnResized: handleChangeColumnState,
            onGridSizeChanged: fitColumnsToGridWidth,
            onDisplayedColumnsChanged: fitColumnsToGridWidth,
            onColumnMenuVisibleChanged: onColumnMenuVisibleChanged,
            onPaginationChanged: onPaginationChanged,
            onSortChanged: onSortChanged,
            enableRtl: enableRtl,
            getLocaleText: getLocaleText,
            ...agGridDocumentsProps,
            rowModelType: 'serverSide',
            suppressServerSideFullWidthLoadingRow: true,
            columnTypes: columnTypes,
            getRowId: getRowID,
            rowHeight: 58,
            columnDefs: columnDefs,
            context: gridContext,
            cacheBlockSize: perPage,
            maxBlocksInCache: 1,
            initialState: initialState,
            noRowsOverlayComponent: NoRowsOverlay,
            serverSideInitialRowCount: paymentsTotal || 1,
            pagination: true,
            paginationPageSize: perPage,
            paginationPageSizeSelector: GRID_PAGE_SIZE,
            processUnpinnedColumns: handleProcessUnpinnedColumns,
          } as any)}
        />
      </Box>

      {menu?.status && (
        <DocumentContextMenu
          open={menu?.status}
          anchor={menu?.anchor}
          onClose={contextMenuHide}
          options={contextMenuItems}
          getDocumentLinkedCountByContextMenu={null}
        />
      )}

      {isPreviewOpen && (
        <PaymentPreviewNavigation
          id={previewState.id}
          setPreview={handleChangePreview}
          list={paymentsList}
          allCount={paymentsCount}
          onContextMenu={handleContextMenu}
          onClick={onPaymentOpen}
          startIndex={startIndex}
          onNavigate={handlePreviewNavigate}
        />
      )}

      <BulkActionPanelWrapper
        isVisible={Boolean(selected.size)}
        count={selected.size}
        countTitle={formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.title',
          defaultMessage: 'Payments selected',
        })}
        onClearSelected={onClearSelected}
      >
        <BulkActions
          disabled={Boolean(previewState.id)}
          paymentsIds={selected as any}
          clearSelection={onClearSelected}
          selectAll={handleSelectAll}
        />
      </BulkActionPanelWrapper>
    </>
  );
});

const GridWrapper = ({ forwardedRef, ...props }: { forwardedRef?: React.Ref<unknown>; [key: string]: any }) => (
  <Grid ref={forwardedRef} {...props} />
);

export default GridWrapper;
